import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import styled from 'styled-components'
import Tooltip from '@mui/material/Tooltip'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { osMapStyle, waitForMapLibre } from 'utils/maplibre'
import { datasetColour, GROUP_ICONS } from './planningConstraintsMeta'

const TILE_BASE = 'https://tiles.planning.data.gov.uk'
const TILE_MAX_ZOOM = 15 // GOV.UK serves vector tiles up to z15; MapLibre overzooms beyond
const DEFAULT_ZOOM = 15.5
const CORE_DATASETS = ['flood-risk-zone', 'conservation-area', 'listed-building']

// `listed-building` is a point dataset; its LPA-supplied footprints live in a
// sibling dataset. Both are switched by the one "Listed building" toggle.
const COMPANION_LAYERS = { 'listed-building': ['listed-building-outline'] }

const sourceId = dataset => `pdg-${dataset}`
const layerIds = dataset => ({
  fill: `pdg-${dataset}-fill`,
  line: `pdg-${dataset}-line`,
  point: `pdg-${dataset}-point`
})

const datasetsToRender = dataset => [dataset, ...(COMPANION_LAYERS[dataset] || [])]

// Borough- or region-sized designations would otherwise stack into a mud of
// colour over the whole viewport; keep them as a light wash with a clear edge.
const WIDE_AREA_DATASETS = new Set([
  'air-quality-management-area', 'central-activities-zone', 'archaeological-priority-area',
  'flood-risk-zone', 'green-belt', 'national-park', 'area-of-outstanding-natural-beauty',
  'nutrient-neutrality-catchment', 'world-heritage-site-buffer-zone', 'article-4-direction-area'
])
const fillOpacityFor = dataset => (WIDE_AREA_DATASETS.has(dataset) ? 0.1 : 0.3)

/**
 * Map of the planning.data.gov.uk designations around the building, streamed
 * as vector tiles straight from GOV.UK (public, CORS-enabled). Toggles mirror
 * the dataset filters on their own map; flagged datasets start switched on.
 */
const PlanningConstraintsMap = ({ dashboard, height = 560 }) => {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const popupRef = useRef(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(true)

  const lat = dashboard?.location?.latitude
  const lon = dashboard?.location?.longitude
  const hasPoint = typeof lat === 'number' && typeof lon === 'number'

  const items = useMemo(
    () => (dashboard?.groups || []).flatMap(g => g.items.map(i => ({ ...i, groupKey: g.key, groupLabel: g.label }))),
    [dashboard]
  )
  const flagged = useMemo(() => items.filter(i => i.status === 'flagged').map(i => i.dataset), [items])
  // Read at bootstrap only, so the map is not torn down when the dashboard refreshes.
  const itemsRef = useRef(items)
  itemsRef.current = items

  const [enabled, setEnabled] = useState(() => new Set(flagged.length ? flagged : CORE_DATASETS))

  useEffect(() => {
    setEnabled(new Set(flagged.length ? flagged : CORE_DATASETS))
  }, [flagged])

  // --- map bootstrap ---------------------------------------------------------
  useEffect(() => {
    if (!hasPoint || !containerRef.current || mapRef.current) return undefined
    if (!osMapStyle) {
      setError('Ordnance Survey map key is not configured.')
      return undefined
    }

    let disposed = false

    waitForMapLibre()
      .then(maplibregl => {
        if (disposed || !containerRef.current || mapRef.current) return

        const map = new maplibregl.Map({
          container: containerRef.current,
          style: osMapStyle,
          center: [lon, lat],
          zoom: DEFAULT_ZOOM,
          minZoom: 6,
          maxZoom: 19,
          attributionControl: false
        })
        mapRef.current = map
        containerRef.current.pdgMap = map // debugging handle (e2e / console)
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
        map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left')

        const marker = document.createElement('div')
        marker.className = 'pdg-building-marker'
        marker.title = 'This building'
        new maplibregl.Marker({ element: marker, anchor: 'center' }).setLngLat([lon, lat]).addTo(map)

        // The container can be measured at 0×0 while the panel is still laying
        // out, which leaves MapLibre on its 400×300 default. Track the real size.
        const resizeObserver = new ResizeObserver(() => map.resize())
        resizeObserver.observe(containerRef.current)
        map.once('remove', () => resizeObserver.disconnect())

        map.on('load', () => {
          if (disposed) return
          map.resize()
          const allDatasets = [...new Set(itemsRef.current.flatMap(i => datasetsToRender(i.dataset)))]
          allDatasets.forEach(dataset => addDatasetLayers(map, dataset))
          wirePopups(map, maplibregl, allDatasets, popupRef)
          setReady(true)
        })
        map.on('error', e => {
          // Tile 404s for empty areas are normal; only surface style failures.
          if (e?.error?.message && /style/i.test(e.error.message)) setError(e.error.message)
        })
      })
      .catch(() => setError('Could not load the map library.'))

    return () => {
      disposed = true
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
      setReady(false)
    }
  }, [hasPoint, lat, lon])

  // --- apply toggles ---------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    items.forEach(item => {
      const visible = enabled.has(item.dataset) ? 'visible' : 'none'
      datasetsToRender(item.dataset).forEach(dataset => {
        Object.values(layerIds(dataset)).forEach(id => {
          if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible)
        })
      })
    })
  }, [enabled, items, ready])

  const toggle = useCallback(dataset => {
    setEnabled(prev => {
      const next = new Set(prev)
      if (next.has(dataset)) next.delete(dataset)
      else next.add(dataset)
      return next
    })
  }, [])

  const recentre = useCallback(() => {
    mapRef.current?.flyTo({ center: [lon, lat], zoom: DEFAULT_ZOOM })
  }, [lat, lon])

  if (!hasPoint) {
    return (
      <MapNotice>
        The map needs the building&apos;s coordinates. They are filled in automatically when the address is
        set from the UPRN lookup; edit the address to add them.
      </MapNotice>
    )
  }

  const groups = dashboard?.groups || []

  return (
    <MapFrame style={{ height }}>
      <MapCanvas ref={containerRef} />

      {error && <MapNotice $overlay>{error}</MapNotice>}
      {!ready && !error && <MapNotice $overlay>Loading map…</MapNotice>}

      <FiltersToggle type='button' onClick={() => setFiltersOpen(v => !v)} aria-expanded={filtersOpen}>
        <LayersOutlinedIcon fontSize='small' /> Layers ({enabled.size})
      </FiltersToggle>

      {filtersOpen && (
        <Filters>
          <FiltersHeader>
            <span>Datasets</span>
            <QuickActions>
              <button type='button' onClick={() => setEnabled(new Set(flagged))} disabled={!flagged.length}>Flagged</button>
              <button type='button' onClick={() => setEnabled(new Set(items.map(i => i.dataset)))}>All</button>
              <button type='button' onClick={() => setEnabled(new Set())}>None</button>
            </QuickActions>
          </FiltersHeader>
          <FiltersScroll>
            {groups.map(group => {
              const GroupIcon = GROUP_ICONS[group.key]
              return (
                <FilterGroup key={group.key}>
                  <FilterGroupTitle>
                    {GroupIcon && <GroupIcon fontSize='inherit' />}
                    {group.label}
                  </FilterGroupTitle>
                  {group.items.map(item => (
                    <FilterRow key={item.dataset} $on={enabled.has(item.dataset)}>
                      <input
                        type='checkbox'
                        id={`pdg-filter-${item.dataset}`}
                        checked={enabled.has(item.dataset)}
                        onChange={() => toggle(item.dataset)}
                      />
                      <Swatch style={{ background: datasetColour(item.dataset) }} />
                      <label htmlFor={`pdg-filter-${item.dataset}`}>{item.label}</label>
                      {item.status === 'flagged' && (
                        <Tooltip title={`${item.count} ${item.count === 1 ? 'designation applies' : 'designations apply'} to this building`}>
                          <FlagBadge $severity={item.severity}>{item.count}</FlagBadge>
                        </Tooltip>
                      )}
                    </FilterRow>
                  ))}
                </FilterGroup>
              )
            })}
          </FiltersScroll>
          <FiltersFooter>
            <button type='button' onClick={recentre}>Back to building</button>
            <span>Tiles © planning.data.gov.uk · Base map © Ordnance Survey</span>
          </FiltersFooter>
        </Filters>
      )}
    </MapFrame>
  )
}

export default PlanningConstraintsMap

// ---------------------------------------------------------------------------

const addDatasetLayers = (map, dataset) => {
  const src = sourceId(dataset)
  const ids = layerIds(dataset)
  const colour = datasetColour(dataset)
  // Designations with an end-date in the past no longer apply: draw them faded.
  const isHistoric = ['all', ['!=', ['coalesce', ['get', 'end-date'], ''], ''], ['<', ['coalesce', ['get', 'end-date'], ''], new Date().toISOString().slice(0, 10)]]
  const paintColour = ['case', isHistoric, '#9ca3af', colour]

  if (!map.getSource(src)) {
    map.addSource(src, {
      type: 'vector',
      tiles: [`${TILE_BASE}/${dataset}/{z}/{x}/{y}.pbf`],
      minzoom: 6,
      maxzoom: TILE_MAX_ZOOM
    })
  }

  if (!map.getLayer(ids.fill)) {
    map.addLayer({
      id: ids.fill,
      type: 'fill',
      source: src,
      'source-layer': dataset,
      filter: ['==', ['geometry-type'], 'Polygon'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': paintColour, 'fill-opacity': fillOpacityFor(dataset) }
    })
  }
  if (!map.getLayer(ids.line)) {
    map.addLayer({
      id: ids.line,
      type: 'line',
      source: src,
      'source-layer': dataset,
      filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'LineString']],
      layout: { visibility: 'none' },
      paint: { 'line-color': paintColour, 'line-width': WIDE_AREA_DATASETS.has(dataset) ? 2.5 : 1.5, 'line-opacity': 0.9 }
    })
  }
  if (!map.getLayer(ids.point)) {
    map.addLayer({
      id: ids.point,
      type: 'circle',
      source: src,
      'source-layer': dataset,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: { visibility: 'none' },
      paint: {
        'circle-color': paintColour,
        'circle-opacity': 0.85,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 15, 7, 18, 10],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5
      }
    })
  }
}

const humanise = value => String(value || '').replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())

const wirePopups = (map, maplibregl, datasets, popupRef) => {
  const clickable = datasets.flatMap(d => [layerIds(d).fill, layerIds(d).point])

  clickable.forEach(id => {
    map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', id, () => { map.getCanvas().style.cursor = '' })
  })

  map.on('click', e => {
    const features = map.queryRenderedFeatures(e.point, { layers: clickable.filter(id => map.getLayer(id)) })
    if (!features.length) return

    // De-duplicate: the same polygon can be split across tile boundaries.
    const seen = new Set()
    const rows = []
    for (const f of features) {
      const p = f.properties || {}
      const key = `${p.dataset || f.sourceLayer}:${p.entity}`
      if (seen.has(key)) continue
      seen.add(key)
      rows.push(p)
    }

    const html = rows.slice(0, 6).map(p => {
      const dataset = p.dataset || ''
      const entityUrl = p.entity ? `https://www.planning.data.gov.uk/entity/${p.entity}` : null
      const meta = [
        p.reference && `Ref ${escapeHtml(p.reference)}`,
        p['start-date'] && `since ${escapeHtml(p['start-date'])}`,
        p['end-date'] && `ended ${escapeHtml(p['end-date'])}`
      ].filter(Boolean).join(' · ')
      return `
        <div class="pdg-popup-row">
          <div class="pdg-popup-dataset"><span class="pdg-popup-swatch" style="background:${datasetColour(dataset)}"></span>${escapeHtml(humanise(dataset))}</div>
          <div class="pdg-popup-name">${escapeHtml(p.name || humanise(dataset))}</div>
          ${meta ? `<div class="pdg-popup-meta">${meta}</div>` : ''}
          ${entityUrl ? `<a class="pdg-popup-link" href="${entityUrl}" target="_blank" rel="noreferrer">View on planning.data.gov.uk ↗</a>` : ''}
        </div>`
    }).join('')

    popupRef.current?.remove()
    popupRef.current = new maplibregl.Popup({ closeButton: true, maxWidth: '320px' })
      .setLngLat(e.lngLat)
      .setHTML(`<div class="pdg-popup">${html}${rows.length > 6 ? `<div class="pdg-popup-meta">+${rows.length - 6} more</div>` : ''}</div>`)
      .addTo(map)
  })
}

const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// ---------------------------------------------------------------------------

const MapFrame = styled.div`
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #e9edf3;
  border: 1px solid #f2f2f2;

  .pdg-building-marker {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #d24724;
    border: 3px solid #fff;
    box-shadow: 0 0 0 3px rgba(210, 71, 36, 0.35), 0 2px 6px rgba(0, 0, 0, 0.3);
  }

  .maplibregl-popup-content {
    padding: 10px 12px;
    border-radius: 8px;
    font-family: inherit;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
  .pdg-popup { display: flex; flex-direction: column; gap: 8px; max-height: 260px; overflow-y: auto; }
  .pdg-popup-row { padding-bottom: 8px; border-bottom: 1px solid #f2f2f2; }
  .pdg-popup-row:last-child { border-bottom: 0; padding-bottom: 0; }
  .pdg-popup-dataset { font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #666; display: flex; align-items: center; gap: 5px; }
  .pdg-popup-swatch { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
  .pdg-popup-name { font-size: 13px; font-weight: 600; color: #0c1229; margin-top: 2px; }
  .pdg-popup-meta { font-size: 11px; color: #888; margin-top: 2px; }
  .pdg-popup-link { font-size: 11px; color: #33becc; text-decoration: none; display: inline-block; margin-top: 4px; }
  .pdg-popup-link:hover { text-decoration: underline; }
`

const MapCanvas = styled.div`
  /* maplibre-gl.css forces .maplibregl-map to position: relative, so size
     the container explicitly instead of relying on inset. */
  width: 100%;
  height: 100%;
`

const MapNotice = styled.div`
  ${({ $overlay }) => $overlay
    ? 'position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 2;'
    : 'margin: 0 0 16px 0;'}
  background: #fff;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  color: #666;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.06);
  max-width: 480px;
`

const FiltersToggle = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #f2f2f2;
  background: #fff;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #0c1229;
  cursor: pointer;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.08);
`

const Filters = styled.div`
  position: absolute;
  top: 52px;
  left: 12px;
  bottom: 12px;
  z-index: 3;
  width: 260px;
  max-width: calc(100% - 24px);
  background: #fff;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 220px;
  }
`

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #f2f2f2;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  color: #0c1229;
`

const QuickActions = styled.div`
  display: flex;
  gap: 4px;

  button {
    font: inherit;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    padding: 3px 7px;
    border-radius: 10px;
    border: 1px solid #e6e6e6;
    background: #fff;
    color: #555;
    cursor: pointer;
  }
  button:hover:not(:disabled) { border-color: #33becc; color: #0c1229; }
  button:disabled { opacity: 0.5; cursor: default; }
`

const FiltersScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 0;
`

const FilterGroup = styled.div`
  padding: 6px 12px 8px 12px;
`

const FilterGroupTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #999;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  color: ${({ $on }) => ($on ? '#0c1229' : '#666')};

  input { margin: 0; accent-color: #33becc; cursor: pointer; }
  label { flex: 1; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`

const Swatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
  opacity: 0.85;
`

const FlagBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  padding: 1px 5px;
  border-radius: 9px;
  color: #fff;
  background: ${({ $severity }) => ($severity === 'critical' ? '#dc2626' : $severity === 'warning' ? '#f97316' : '#3b82f6')};
`

const FiltersFooter = styled.div`
  padding: 8px 12px;
  border-top: 1px solid #f2f2f2;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 10px;
  color: #999;

  button {
    align-self: flex-start;
    font: inherit;
    font-size: 11px;
    font-weight: 500;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid #e6e6e6;
    background: #fff;
    color: #0c1229;
    cursor: pointer;
  }
  button:hover { border-color: #33becc; }
`
